import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject, takeUntil } from 'rxjs';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

/**
 * LMC Design Guideline Preview Component
 *
 * This component showcases the LMC design guideline and reusable components
 * for the LMCredit application. It serves as a living style guide
 * demonstrating:
 * - Material Design card variants
 * - Table styles and layouts
 * - Color palette from _variables.scss
 * - Typography system
 * - Dialog patterns (confirmation, alert, form, information, tool)
 *
 * Recreated from original design-system.component.scss (994 lines)
 * Global reusable styles: assets/styles/scss/*.scss
 * Preview-specific styles: design-system.component.scss
 */
@Component({
  selector: 'app-design-system',
  templateUrl: './design-system.component.html',
  styleUrls: ['./design-system.component.scss']
})
export class DesignSystemComponent implements OnInit {

  // Sample data for table examples
  displayedColumns: string[] = ['id', 'name', 'email', 'status', 'amount', 'actions'];
  dataSource = [
    { id: 1, name: 'John Doe', email: 'john@example.com', status: 'Active', amount: 50000 },
    { id: 2, name: 'Jane Smith', email: 'jane@example.com', status: 'Pending', amount: 75000 },
    { id: 3, name: 'Bob Johnson', email: 'bob@example.com', status: 'Approved', amount: 100000 },
    { id: 4, name: 'Alice Williams', email: 'alice@example.com', status: 'Rejected', amount: 25000 },
    { id: 5, name: 'Charlie Brown', email: 'charlie@example.com', status: 'Active', amount: 60000 }
  ];

  // Sample data for cards
  cardData = [
    {
      title: 'Total Applications',
      value: 1234,
      icon: 'description',
      trend: '+12%',
      trendPositive: true
    },
    {
      title: 'Approved Loans',
      value: 890,
      icon: 'check_circle',
      trend: '+8%',
      trendPositive: true
    },
    {
      title: 'Pending Review',
      value: 234,
      icon: 'pending',
      trend: '-3%',
      trendPositive: false
    },
    {
      title: 'Total Amount',
      value: '$5.2M',
      icon: 'attach_money',
      trend: '+15%',
      trendPositive: true
    }
  ];

  // Sample data for the <lmc-status-breakdown> component
  statusBreakdownItems = [
    { label: 'Queued', count: 12, color: '#7986cb' },
    { label: 'Initiated', count: 3, color: '#4351AF' },
    { label: 'Processing', count: 8, color: '#ff9800' },
    { label: 'Accepted', count: 95, color: '#4caf50' },
    { label: 'Returned', count: 4, color: '#e57373' },
    { label: 'Cancelled', count: 2, color: '#9e9e9e' },
    { label: 'Invalid', count: 3, color: '#f57c00' },
    { label: 'Error', count: 1, color: '#d32f2f' }
  ];

  // Sample data for form examples
  sampleCountries = [
    { value: 'us', label: 'United States' },
    { value: 'ca', label: 'Canada' },
    { value: 'mx', label: 'Mexico' }
  ];

  sampleGenders = [
    { value: 'male', label: 'Male' },
    { value: 'female', label: 'Female' },
    { value: 'other', label: 'Other' }
  ];

  constructor(public dialog: MatDialog) { }

  ngOnInit(): void {
  }

  /**
   * Sample action handler for table actions
   */
  onEdit(element: any): void {
    console.log('Edit:', element);
  }

  onDelete(element: any): void {
    console.log('Delete:', element);
  }

  /**
   * Get status badge class based on status
   */
  getStatusClass(status: string): string {
    const statusMap: Record<string, string> = {
      'Active': 'status-active',
      'Pending': 'status-pending',
      'Approved': 'status-approved',
      'Rejected': 'status-rejected'
    };
    return statusMap[status] || '';
  }

  /**
   * Open confirmation dialog example
   */
  openConfirmationDialog(): void {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '400px',
      maxWidth: '90vw',
      data: {
        title: 'Confirm Action',
        message: 'Are you sure you want to proceed with this action?',
        confirmText: 'Confirm',
        cancelText: 'Cancel'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      console.log('Confirmation dialog result:', result);
    });
  }

  /**
   * Open alert dialog example
   */
  openAlertDialog(): void {
    this.dialog.open(AlertDialogComponent, {
      width: '400px',
      maxWidth: '90vw',
      data: {
        title: 'Warning',
        message: 'This action cannot be undone. Please make sure you want to continue.'
      }
    });
  }

  /**
   * Open form dialog example
   */
  openFormDialog(): void {
    const dialogRef = this.dialog.open(FormDialogComponent, {
      width: '600px',
      maxWidth: '90vw'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        console.log('Form dialog result:', result);
      }
    });
  }

  /**
   * Open info dialog example
   */
  openInfoDialog(): void {
    this.dialog.open(InfoDialogComponent, {
      width: '500px',
      maxWidth: '90vw',
      data: {
        applicationId: 'LA-2024-001234',
        applicant: 'John Doe',
        amount: 50000,
        status: 'Approved'
      }
    });
  }

  /**
   * Open tool dialog example.
   *
   * Wide and tall rather than sized to its text: a working surface, not a message. Note
   * that `autoFocus` is left at its default so the focus trap honours the [cdkFocusInitial]
   * on the dialog's search field — passing `autoFocus: false` would silently disable it.
   */
  openToolDialog(): void {
    const dialogRef = this.dialog.open(ToolDialogComponent, {
      width: '95vw',
      maxWidth: '900px',
      restoreFocus: true,
      data: { rows: this.dataSource }
    });

    dialogRef.afterClosed().subscribe(result => {
      console.log('Tool dialog picked:', result);
    });
  }
}

// ---------------------------------------------------------------------------
// Dialog examples
//
// Each dialog is a standalone component with inline styles, because a dialog renders into
// the CDK overlay — outside this component's DOM — so design-system.component.scss cannot
// reach it. `inlineStyleLanguage: 'scss'` is set in angular.json, so these blocks are SCSS
// and can pull in the shared variables.
//
// EVERY dialog here uses the same shape: a title row carrying a close cross, and an actions
// bar only when there is a real decision or a real primary action to put in it. A button
// whose only job is to close duplicates the cross and is left out.
//
//   Confirmation  cross + [Cancel] [Confirm]   Cancel is an answer ("no"), not a close
//   Alert         cross                        acknowledge-only; the cross acknowledges
//   Form          cross + [Save]               the cross discards, Save commits
//   Information   cross                        nothing to decide
//   Tool          cross                        a working surface
//
// The header CSS is shared through DIALOG_HEADER_STYLES below rather than copied five
// times, because two of its rules are non-obvious and must not drift between dialogs.
// ---------------------------------------------------------------------------

/**
 * The title row every dialog shares: title on the left, close cross on the right.
 *
 * Two rules here are traps rather than choices, which is why this is one shared constant:
 *
 *  1. **The dialog surface has no padding of its own.** `_dialogs.scss` puts the inset on
 *     the title and content elements, and this header row is neither — without its own
 *     padding the cross lands jammed in the surface's top-right corner. Right is 1rem, not
 *     1.5rem: an icon button carries ~8px around its 24px glyph, so 16px here puts the
 *     glyph's edge on the same 24px line as the content below. Top is 0.5rem — the 40px
 *     button, not the 32px title, sets this row's height and already adds 8px above
 *     the glyph.
 *
 *  2. **align-items: center centres the boxes, and MDC inflates the title's box without
 *     moving its text.** MDC gives every dialog title a `::before` strut — an empty 40px
 *     inline-block, baseline-aligned — which pushes the line box 40px above the baseline
 *     and leaves the text at the BOTTOM of a ~47px box, roughly 10px below the glyph's
 *     centre. Drop the strut and the h2 is just its 32px line box: both centres land
 *     at 20px.
 *
 * `.dialog-title-icon` is the opt-in for a title that also carries a leading icon. That
 * layout is not inherited either — the rule in `_dialogs.scss` that would supply it sits
 * inside a `::ng-deep` block in a global stylesheet, where the selector is invalid and the
 * whole block is dead (the file says so itself).
 */
const DIALOG_HEADER_STYLES = `
  @import 'src/assets/styles/scss/variables';

  .dialog-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.5rem 1rem 0 0;

    .lmc-dialog-title {
      padding-bottom: 0;

      // See note 2 above: without this the title sits ~10px below the cross.
      &::before {
        display: none;
      }
    }

    // Opt-in: a title with a leading icon.
    .dialog-title-icon {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    .close-button {
      flex-shrink: 0;
      color: #666;
    }
  }
`;

/**
 * 1. Confirmation Dialog — a yes/no question.
 *
 * The only pattern that keeps a Cancel button. Cancel is not a duplicate of the cross: it
 * returns an explicit `false`, and the two answers being side by side is what makes the
 * question readable as a question.
 */
@Component({
  selector: 'app-confirmation-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule
  ],
  template: `
    <div class="dialog-header">
      <h2 mat-dialog-title class="lmc-dialog-title dialog-title-icon">
        <mat-icon color="primary">help_outline</mat-icon>
        {{data.title}}
      </h2>
      <button mat-icon-button [mat-dialog-close]="false" type="button"
              class="close-button" aria-label="Dismiss without confirming" matTooltip="Close">
        <mat-icon>close</mat-icon>
      </button>
    </div>

    <mat-dialog-content>
      <p>{{data.message}}</p>
    </mat-dialog-content>

    <!-- Kept: two answers to a question, not one action plus a way out. The cross returns
         the same false as Cancel, so dismissing is never read as consent. -->
    <mat-dialog-actions align="end">
      <button mat-button [mat-dialog-close]="false">{{data.cancelText}}</button>
      <button mat-button color="primary" [mat-dialog-close]="true">
        {{data.confirmText}}
      </button>
    </mat-dialog-actions>
  `,
  styles: [DIALOG_HEADER_STYLES, `
    mat-dialog-content {
      min-width: 300px;
    }
  `]
})
export class ConfirmationDialogComponent {
  readonly dialogRef = inject(MatDialogRef<ConfirmationDialogComponent>);
  readonly data = inject<any>(MAT_DIALOG_DATA);
}

/**
 * 2. Alert Dialog — something the operator needs to see before carrying on.
 *
 * No actions bar: the old "I Understand" button did nothing the cross does not, so it was
 * a duplicate. Note this drops the *forced* acknowledgement the pattern used to have — if
 * a particular alert genuinely must be clicked through, make it a Confirmation instead,
 * where the answer is recorded.
 */
@Component({
  selector: 'app-alert-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule
  ],
  template: `
    <div class="dialog-header">
      <h2 mat-dialog-title class="lmc-dialog-title dialog-title-icon dialog-title-warn">
        <mat-icon color="warn">warning</mat-icon>
        {{data.title}}
      </h2>
      <button mat-icon-button mat-dialog-close type="button"
              class="close-button" aria-label="Close warning" matTooltip="Close">
        <mat-icon>close</mat-icon>
      </button>
    </div>

    <mat-dialog-content>
      <p>{{data.message}}</p>
    </mat-dialog-content>
  `,
  styles: [DIALOG_HEADER_STYLES, `
    @import 'src/assets/styles/scss/variables';

    // The one place a dialog title departs from $primary-900: the title is carrying the
    // warning, so it takes the warning's colour.
    .dialog-title-warn {
      color: $error-700;
    }

    mat-dialog-content {
      min-width: 300px;
      padding-bottom: 1rem !important; // no actions bar to close the frame
    }
  `]
})
export class AlertDialogComponent {
  readonly dialogRef = inject(MatDialogRef<AlertDialogComponent>);
  readonly data = inject<any>(MAT_DIALOG_DATA);
}

/**
 * 3. Form Dialog — collects input and returns it.
 *
 * The actions bar holds Save alone. Cancel went: it closed and returned null, which is
 * exactly what the cross does. Save stays disabled until the form is valid, so the dialog
 * cannot hand back something it knows is wrong.
 */
@Component({
  selector: 'app-form-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatTooltipModule
  ],
  template: `
    <div class="dialog-header">
      <h2 mat-dialog-title class="lmc-dialog-title">Edit Application</h2>
      <button mat-icon-button [mat-dialog-close]="null" type="button"
              class="close-button" aria-label="Discard changes and close" matTooltip="Close">
        <mat-icon>close</mat-icon>
      </button>
    </div>

    <mat-dialog-content>
      <form [formGroup]="form">
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Name</mat-label>
          <input matInput formControlName="name" cdkFocusInitial required>
          <mat-error *ngIf="form.get('name')?.hasError('required')">
            Name is required
          </mat-error>
        </mat-form-field>

        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Email</mat-label>
          <input matInput type="email" formControlName="email" required>
          <mat-error *ngIf="form.get('email')?.hasError('required')">
            Email is required
          </mat-error>
          <mat-error *ngIf="form.get('email')?.hasError('email')">
            Invalid email format
          </mat-error>
        </mat-form-field>

        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Amount</mat-label>
          <input matInput type="number" formControlName="amount" required>
          <span matPrefix>$&nbsp;</span>
          <mat-error *ngIf="form.get('amount')?.hasError('required')">
            Amount is required
          </mat-error>
        </mat-form-field>
      </form>
    </mat-dialog-content>

    <!-- Save only. The cross is the way out; a Cancel beside it would be the same button. -->
    <mat-dialog-actions align="end">
      <button mat-button color="primary"
              (click)="save()"
              [disabled]="form.invalid">
        Save
      </button>
    </mat-dialog-actions>
  `,
  styles: [DIALOG_HEADER_STYLES, `
    mat-dialog-content {
      min-width: 400px;
    }

    .full-width {
      width: 100%;
      margin-bottom: 1rem;
    }
  `]
})
export class FormDialogComponent {
  readonly dialogRef = inject(MatDialogRef<FormDialogComponent>);
  private fb = inject(FormBuilder);

  form: FormGroup;

  constructor() {
    this.form = this.fb.group({
      name: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      amount: ['', Validators.required]
    });
  }

  save(): void {
    if (this.form.valid) {
      this.dialogRef.close(this.form.value);
    }
  }
}

/**
 * 4. Information Dialog — read-only detail.
 *
 * Nothing to decide, so no actions bar. The line between this and a Tool Dialog is length:
 * a handful of facts that fit without scrolling belongs here; a surface the operator works
 * in belongs below.
 */
@Component({
  selector: 'app-info-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule
  ],
  template: `
    <div class="dialog-header">
      <h2 mat-dialog-title class="lmc-dialog-title dialog-title-icon">
        <mat-icon color="primary">info</mat-icon>
        Application Details
      </h2>
      <button mat-icon-button mat-dialog-close type="button"
              class="close-button" aria-label="Close application details" matTooltip="Close">
        <mat-icon>close</mat-icon>
      </button>
    </div>

    <mat-dialog-content>
      <div class="dialog-info-row">
        <span class="label">Application ID:</span>
        <span class="value">{{data.applicationId}}</span>
      </div>
      <div class="dialog-info-row">
        <span class="label">Applicant:</span>
        <span class="value">{{data.applicant}}</span>
      </div>
      <div class="dialog-info-row">
        <span class="label">Amount:</span>
        <span class="value">\${{data.amount | number}}</span>
      </div>
      <div class="dialog-info-row">
        <span class="label">Status:</span>
        <span class="value">
          <span class="status-badge status-approved">{{data.status}}</span>
        </span>
      </div>
    </mat-dialog-content>
  `,
  styles: [DIALOG_HEADER_STYLES, `
    @import 'src/assets/styles/scss/variables';

    mat-dialog-content {
      min-width: 400px;
      padding-bottom: 1rem !important; // no actions bar to close the frame
    }

    .dialog-info-row {
      display: flex;
      justify-content: space-between;
      padding: 0.75rem 0;
      border-bottom: 1px solid #f0f0f0;

      &:last-child {
        border-bottom: none;
      }
    }

    .label {
      font-weight: 500;
      color: #666;
      font-size: 0.875rem;
    }

    .value {
      color: #344054;
      font-size: 0.875rem;
    }

    .status-badge {
      display: inline-block;
      padding: 0.25rem 0.75rem;
      border-radius: 12px;
      font-size: 0.75rem;
      font-weight: 600;
      text-transform: uppercase;

      &.status-approved {
        background-color: rgba($success-500, 0.1);
        color: $success-700;
      }
    }
  `]
})
export class InfoDialogComponent {
  readonly dialogRef = inject(MatDialogRef<InfoDialogComponent>);
  readonly data = inject<any>(MAT_DIALOG_DATA);
}

/**
 * 5. Tool Dialog — a working surface: the operator opens it, uses it, dismisses it.
 *
 * Reference implementation in the app:
 * loan-servicing/payment-processing/search/transaction-search-dialog.component.*
 */
@Component({
  selector: 'app-tool-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatTableModule,
    MatTooltipModule
  ],
  template: `
    <div class="dialog-header">
      <h2 mat-dialog-title class="lmc-dialog-title">Find an Applicant</h2>
      <button mat-icon-button mat-dialog-close type="button"
              class="close-button" aria-label="Close applicant search" matTooltip="Close">
        <mat-icon>close</mat-icon>
      </button>
    </div>

    <mat-dialog-content class="dialog-content">

      <mat-form-field appearance="outline" class="search-field" subscriptSizing="dynamic">
        <mat-label>Name or email</mat-label>
        <mat-icon matPrefix>search</mat-icon>
        <!-- type="text", not "search": WebKit draws its own clear (x) inside a search
             input, which would sit beside the Material one below. -->
        <input matInput type="text" cdkFocusInitial autocomplete="off"
               [formControl]="filterControl" placeholder="e.g. Jane">
        <button *ngIf="filterControl.value"
                mat-icon-button matSuffix type="button"
                aria-label="Clear filter"
                (click)="filterControl.setValue('')">
          <mat-icon>close</mat-icon>
        </button>
      </mat-form-field>

      <div class="results-area">
        <table mat-table [dataSource]="filteredRows" class="flat-table" *ngIf="filteredRows.length">
          <ng-container matColumnDef="name">
            <th mat-header-cell *matHeaderCellDef>Name</th>
            <td mat-cell *matCellDef="let row">{{row.name}}</td>
          </ng-container>
          <ng-container matColumnDef="email">
            <th mat-header-cell *matHeaderCellDef>Email</th>
            <td mat-cell *matCellDef="let row">{{row.email}}</td>
          </ng-container>
          <ng-container matColumnDef="amount">
            <th mat-header-cell *matHeaderCellDef class="align-right">Amount</th>
            <td mat-cell *matCellDef="let row" class="align-right">\${{row.amount | number}}</td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="columns; sticky: true"></tr>
          <tr mat-row *matRowDef="let row; columns: columns"
              class="picker-row"
              role="button" tabindex="0"
              matTooltip="Pick this applicant" matTooltipShowDelay="600"
              (click)="pick(row)" (keydown.enter)="pick(row)"></tr>
        </table>

        <!-- Placeholders share one centred slot, so the dialog reads the same whatever
             state it is in. -->
        <div class="results-placeholder" *ngIf="!filteredRows.length">
          <mat-icon>search_off</mat-icon>
          <p>No applicant matches "{{filterControl.value}}"</p>
          <span>Try part of a name, or an email address.</span>
        </div>
      </div>

    </mat-dialog-content>
  `,
  styles: [DIALOG_HEADER_STYLES, `
    @import 'src/assets/styles/scss/variables';

    // A fixed height, not a max: content loading in must not resize the dialog under the
    // cursor. No actions bar follows, so only the header is subtracted — and the last row
    // needs its own bottom gap.
    .dialog-content {
      display: flex;
      flex-direction: column;
      padding-top: 0.5rem !important;
      padding-bottom: 0.5rem !important;
      height: calc(70vh - 96px);
      max-height: none;
      overflow: hidden;
    }

    .search-field {
      flex: 0 0 auto;
      width: 100%;
      margin-bottom: 0.75rem;

      mat-icon[matPrefix] {
        margin-right: 0.5rem;
        color: $primary-600;
      }
    }

    // The one scroller. min-height: 0 is what lets a flex child shrink below its content
    // and actually scroll.
    .results-area {
      flex: 1 1 auto;
      min-height: 0;
      overflow-y: auto;
    }

    table {
      width: 100%;

      .align-right {
        text-align: right;
      }

      th.mat-mdc-header-cell {
        background: #fff;
      }
    }

    .picker-row {
      cursor: pointer;

      &:hover,
      &:focus-visible {
        background: $primary-50;
      }

      &:focus-visible {
        outline: 2px solid $primary-400;
        outline-offset: -2px;
      }
    }

    // Centred in the results area rather than at its top: the area is a fixed height, so a
    // top-aligned message would leave a large empty box under it.
    .results-placeholder {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 100%;
      padding: 2rem 1rem;
      text-align: center;
      color: #aaa;

      mat-icon {
        font-size: 3rem;
        width: 3rem;
        height: 3rem;
        margin-bottom: 0.75rem;
        color: #ccc;
      }

      p {
        font-size: 1rem;
        font-weight: 500;
        margin: 0 0 0.25rem;
        color: #888;
      }

      span {
        font-size: 0.8125rem;
      }
    }
  `]
})
export class ToolDialogComponent implements OnDestroy {
  readonly dialogRef = inject(MatDialogRef<ToolDialogComponent, unknown>);
  readonly data = inject<{ rows: any[] }>(MAT_DIALOG_DATA);

  readonly columns = ['name', 'email', 'amount'];
  readonly filterControl = new FormControl('', { nonNullable: true });

  filteredRows: any[] = this.data.rows;

  private readonly destroy$ = new Subject<void>();

  constructor() {
    // Filtering is local here, so it reacts on every keystroke. Hitting a server instead
    // needs debounceTime + a switchMap — see transaction-search-dialog.component.ts.
    this.filterControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        const term = value.trim().toLowerCase();
        this.filteredRows = term
          ? this.data.rows.filter((r) =>
              r.name.toLowerCase().includes(term) || r.email.toLowerCase().includes(term))
          : this.data.rows;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Hands the picked row back; the opener decides what to do with it. */
  pick(row: any): void {
    this.dialogRef.close(row);
  }
}
